/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:22:48 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { schemaBuilder, Many2OneUUIDPKBaseSchema, isDate } from "lib/validation";
import { WritableModel } from "lib/schemas/abstract";
import { BillingConfiguration } from "./BillingConfiguration";
import { PrimaryKeyValue, UUID4 } from "lib/types";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
} from "lib/schemas";
import { ICondition, CONDITION_LOGIC_TYPES, ConditionValidationSchema } from "lib/schemas/service-builder/Condition";

enum BILLING_SCHEDULE_INTERVALS {
  once = "once",
  second = "second",
  minute = "minute",
  hour = "hour",
  day = "day",
  week = "week",
  month = "month",
  year = "year",
}

export interface BillingSchedule extends ProcessTemplateArtifact, WritableModel<true> {
  name: string;
  code: string;
  billing_configuration: PrimaryKeyValue<BillingConfiguration> & UUID4;
  price_settings: IPriceSettings;
  reviewers_can_edit_price: boolean;
  interval_start?: string; // Date
  interval_count?: number;
  interval?: keyof typeof BILLING_SCHEDULE_INTERVALS;
  readonly created?: string;
  readonly modified?: string;
}

export interface IPriceSettings {
  price?: number;
  tax?: number; // Should be 0 < n < 1. But typescript does not currently support this type definition - https://stackoverflow.com/questions/39540156/typescript-number-must-be-less-than-1-and-greater-than-0
  price_rules?: IPriceRule[];
}

export interface IPriceRule {
  name: string;
  conditions: ICondition[];
  condition_logic_type: keyof typeof CONDITION_LOGIC_TYPES;
  price: number;
}

export const PriceRuleValidationSchema: schemaBuilder.SchemaOf<IPriceRule> = schemaBuilder.object().shape({
  name: schemaBuilder.string().required("Please give a name for this pricing rule").default("Price Rule"),
  conditions: schemaBuilder
    .array(ConditionValidationSchema)
    .required("You must provide at least one condition for a price rule."),
  condition_logic_type: schemaBuilder
    .mixed()
    .oneOf(Object.keys(CONDITION_LOGIC_TYPES))
    .required("Kindly specify the condition rule."),
  price: schemaBuilder.mixed().required("Price is a required field for a price rule"),
});

export const PriceSettingsValidationSchema: schemaBuilder.SchemaOf<IPriceSettings> = schemaBuilder.object().shape({
  price: schemaBuilder.mixed().nullable().optional(),
  tax: schemaBuilder
    .number()
    .max(1, "Tax value must be less than 100%")
    .min(0, "Tax value must be more than 0%")
    .optional()
    .nullable()
    .typeError("Please enter a number"),
  price_rules: schemaBuilder.array(PriceRuleValidationSchema),
});

type WriteOnlyBillingSchedule = Omit<BillingSchedule, "created" | "modified">;

export const BillingScheduleValidationSchema: ProcessTemplateArtifactSchemaType<WriteOnlyBillingSchedule> =
  ProcessTemplateArtifactValidationSchema.shape({
    id: schemaBuilder.string().uuid().optional(),
    name: schemaBuilder.string().required("Name is required."),
    code: schemaBuilder.string().required("Code is required."),
    billing_configuration: Many2OneUUIDPKBaseSchema().required(
      "A billing configuration is required when adding a fee."
    ),
    reviewers_can_edit_price: schemaBuilder.boolean().optional().nullable(),
    interval: schemaBuilder
      .mixed()
      .oneOf(Object.keys(BILLING_SCHEDULE_INTERVALS))
      .default(BILLING_SCHEDULE_INTERVALS.once)
      .optional()
      .nullable(),
    interval_start: schemaBuilder
      .string()
      .optional()
      .nullable()
      .test("validate-interval-start-date", "The interval start must be a valid date value.", isDate),
    interval_count: schemaBuilder.number().optional().nullable(),
    price_settings: PriceSettingsValidationSchema.optional().nullable(),
  });
